// Extracted from root-shell.tsx (which exports ONLY components — react-refresh
// forbids mixing a hook export into that file).
import { useEffect } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { USE_REAL_API } from './lib/api'
import { isStartCompanyBundle } from './lib/public-bundle'
import { crossOriginRedirect, IS_ADMIN_ORIGIN } from './lib/origin-shell'
import { useOrgContextStore } from './store/orgContext'
import { useAuthStore } from './store/auth'
import { APPEARANCE_ENABLED, useAppearanceStore } from './store/appearance'
import { fetchMe, isAdminOrgType, shellForOrgType, type Shell } from './lib/auth'

// Public routes that never require a token (the login entry + onboarding).
const PUBLIC_PREFIXES = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/accept',
  '/apply',
  '/onboarding',
  '/start-company',
  '/start-a-company',
]

// Local (non-exported) copy of the bundle flag — router.tsx exports the
// canonical IS_START_COMPANY_BUNDLE for route wiring; importing it here would
// be circular. isStartCompanyBundle() is pure, so both read the same value.
const IS_START_COMPANY_BUNDLE = isStartCompanyBundle()

export function useRealAuthGuard(): { blockAppShell: boolean } {
  const navigate = useNavigate()
  const accessToken = useAuthStore((s) => s.accessToken)
  const clearTokens = useAuthStore((s) => s.clearTokens)
  const hydrateAppearance = useAppearanceStore((s) => s.hydrateFromAccount)
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  // Resolve the logged-in user's org_type: on the ADMIN origin, so a non-admin
  // (seller/supply) account with a lingering admin-origin token can be rejected;
  // on the APP origin, so a seller who types /supply (or the reverse) is bounced
  // to their own shell instead of the other one's chrome mounting for real data
  // the backend then correctly refuses (P2-29 audit: "ekran boş/403").
  //
  // Keyed by the token itself: a bare ['me'] key would still read the PREVIOUS
  // session's cached identity for a few minutes after a same-tab logout+login
  // (a seller signing out, a supply account signing in), bouncing the new
  // session on a stale read. staleTime is a per-token cache, not a wall clock.
  const { data: me } = useQuery({
    queryKey: ['me', accessToken],
    queryFn: fetchMe,
    enabled: USE_REAL_API && Boolean(accessToken),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: APPEARANCE_ENABLED ? 'always' : false,
  })

  useEffect(() => {
    if (!APPEARANCE_ENABLED || !USE_REAL_API || !accessToken || !me?.user?.id) return
    if (IS_ADMIN_ORIGIN && !isAdminOrgType(me.active_membership?.org_type)) return
    hydrateAppearance(me.user.id, me.user.appearance_preference ?? 'system')
  }, [accessToken, hydrateAppearance, me])

  // App origin = seller + supply share it (D-7). crossOriginRedirect only
  // separates admin from app; it never distinguishes /seller from /supply, so
  // an account typing the other shell's path would otherwise mount that
  // shell's chrome for real — the backend refuses the data (empty/403) but the
  // shell itself looks like a leaked feature (P2-29 audit).
  // A platform admin who entered a customer's organisation renders that
  // organisation's surface on THIS origin (see crossOriginRedirect).
  const viewingAsOrg = useOrgContextStore((s) => s.mode === 'platform_viewing')
  const onSeller = !IS_ADMIN_ORIGIN && pathname.startsWith('/seller')
  const onSupply = !IS_ADMIN_ORIGIN && pathname.startsWith('/supply')
  const checkingAppShell = USE_REAL_API && Boolean(accessToken) && (onSeller || onSupply)
  // undefined = the /auth/me round trip has not settled yet. A null org_type is
  // left alone rather than guessed — the same "never guess" invariant
  // shellForOrgType/isAdminOrgType carry elsewhere — so it never blocks.
  const realShell: Shell | null | undefined = checkingAppShell
    ? me === undefined
      ? undefined
      : shellForOrgType(me?.active_membership?.org_type)
    : null
  const appShellMismatch =
    realShell != null && ((onSeller && realShell !== '/seller') || (onSupply && realShell !== '/supply'))
  // Covers both directions of the flash: while /auth/me is still in flight, and
  // for the one commit between a mismatch being found and the redirect below
  // actually landing — the wrong shell's chrome is never painted either way.
  const blockAppShell = checkingAppShell && (realShell === undefined || appShellMismatch)

  useEffect(() => {
    // Standalone public wizard bundle: it has no protected surface — the bundle
    // root "/" IS the public page — so the real-auth guard must never run here
    // (otherwise it would redirect the anonymous applicant to /login).
    if (IS_START_COMPANY_BUNDLE) return
    // Origin isolation (Phase 1): a route belonging to the other origin's shell
    // (e.g. /admin reached on the app origin) is bounced to its own origin so
    // the admin token only ever lands in admin.pexify.net storage. Inert unless
    // both origins are wired at build time.
    const elsewhere = crossOriginRedirect(pathname, { viewingAsOrg })
    if (elsewhere) {
      window.location.assign(elsewhere)
      return
    }
    // Maket/simulation mode: the mock role-switcher drives — no guard at all.
    if (!USE_REAL_API) return
    // Real-engine mode: a missing token forces /login (public pages excepted).
    if (!accessToken) {
      if (PUBLIC_PREFIXES.some((p) => pathname.startsWith(p))) return
      navigate({ to: '/login' })
      return
    }
    // Admin origin = A1/A2 ONLY. A non-admin (seller/supply) account that still
    // holds an admin-origin token (logged in here before this gate) is signed
    // out → /login, where the login handler rejects the non-admin account. This
    // clears the token rather than redirecting between shells, so it CANNOT
    // ping-pong with the cross-origin redirect above (no loop).
    if (IS_ADMIN_ORIGIN && me && !isAdminOrgType(me.active_membership?.org_type)) {
      clearTokens()
      navigate({ to: '/login' })
      return
    }
    if (appShellMismatch && realShell) {
      navigate({ to: realShell })
    }
  }, [accessToken, pathname, navigate, me, clearTokens, appShellMismatch, realShell, viewingAsOrg])

  return { blockAppShell }
}
